import { Component, OnInit, Input, Output, EventEmitter, OnChanges, signal, computed } from '@angular/core';
import { ConfigSchema } from '../../../models/config-schema.model';
import { FlowObjectInstance, FlowObjectInstanceState } from '../../../models/flow-object.model';
import { OutputDataTaskMidwayOutboundApi } from '../../../models/input-output-data.model';
import { FlowObjectInstanceService } from '../../../services/flow-object-instance.service';
import { Utils } from '../../../shared/utils';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fob-midway-outbound-api',
    templateUrl: './fob-midway-outbound-api.component.html',
    styleUrl: './fob-midway-outbound-api.component.scss',
    standalone: true,
    imports: IMPORTS
})

export class FlowObjectMidwayOutboundApiComponent implements OnInit, OnChanges {
    // #region [Type props]
    FlowObjectInstanceState = FlowObjectInstanceState;
    Utils = Utils;
    // #endregion

    // #region [const]
    MAX_RETRIES: number = 25 as const;
    RETRY_INTERVAL_SECONDS: number = 3 as const;
    // #endregion

    // #region [props]
    interval: any;
    // #endregion

    // #region [signals]
    $model = signal(null as FlowObjectInstance);
    $message = signal('');
    $isPollingFinished = signal(false);
    $pollingCount = signal(0);
    // #endregion

    // #region [computeds]
    $configSchema = computed(() => JSON.parse(this.$model().flowObjectDefinition.configSchema) as ConfigSchema);
    $pollingProgress = computed(() => this.$pollingCount() * (100 / this.MAX_RETRIES));
    $hasErrors = computed(() => {
        const outputData = JSON.parse(this.$model().outputData) as OutputDataTaskMidwayOutboundApi;
        return outputData.hasNonHttp2xxErrors || outputData.hasOutboundErrors;
    });
    // #endregion

    // #region [Input/Output]
    @Input() _model: FlowObjectInstance;
    @Output() _modelChangeEvent = new EventEmitter<FlowObjectInstance>();
    // #endregion

    constructor(private flowObjectInstanceService: FlowObjectInstanceService) { }

    async ngOnInit() {
        if (
            this.$model().stateId == FlowObjectInstanceState.Started
            && (
                this.$configSchema().taskOutboundApi.backgroundExecution != null
                && this.$configSchema().taskOutboundApi.backgroundExecution === true
            )
        ) {
            this.pollForUpdate();
        } else {
            this.$isPollingFinished.set(true);
            this.fillMessage();
        }
    }

    ngOnChanges() {
        this.$model.set(this._model);
    }

    // #region [===== private methods =====]
    // #endregion

    private pollForUpdate() {
        this.$pollingCount.set(0);
        this.interval = setInterval(() => {
            if (this.MAX_RETRIES != 0 && this.$pollingCount() == this.MAX_RETRIES + 1) {
                clearInterval(this.interval);
                this.$isPollingFinished.set(true);
                this.fillMessage();
                return;
            }

            this.getFlowObjectInstance();
            this.$pollingCount.set(this.$pollingCount() + 1);
        }, this.RETRY_INTERVAL_SECONDS * 1000);
    }

    private async getFlowObjectInstance() {
        const response = await this.flowObjectInstanceService.getById(this.$model().id);

        if (!response.isSuccess) return;

        if (response.data.stateId === FlowObjectInstanceState.Finished) {
            clearInterval(this.interval);
            this.$isPollingFinished.set(true);
            this.$model.set(response.data);
            this._modelChangeEvent.emit(this.$model());
            this.fillMessage();
        }
    }

    private fillMessage() {
        if (Utils.isNullOrEmpty(this.$model()?.outputData)) {
            this.$message.set(this.$configSchema().taskOutboundApi.errorMessage);
        } else {
            const outputData = JSON.parse(this.$model()?.outputData) as OutputDataTaskMidwayOutboundApi;
            if (Utils.isNotNullOrEmpty(outputData.response?.mensagem)) {
                this.$message.set(outputData.response.mensagem);
            } else if (Utils.isString(outputData.response)) {
                this.$message.set(outputData.response);
            }
        }
    }
}
